import React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";

import { useFormik } from "formik";
import * as yup from "yup";

import { useGroupsStore } from "../../store/groupsStore";

interface CreateGroupFields {
  name: string;
  description: string;
  hashtag: string;
  logoUrl: string;
}

const initialValues: CreateGroupFields = {
  name: "",
  description: "",
  hashtag: "",
  logoUrl: "",
};

const validationSchema = yup.object({
  name: yup.string().required("Name is required."),
  description: yup.string().required("Description is required."),
  hashtag: yup
    .string()
    .required("Hashtag is required.")
    .matches(/^[a-zA-Z0-9_]+$/, "Hashtag may only contain letters, numbers, and underscores."),
  logoUrl: yup.string().url("Must be a valid URL.").optional(),
});

interface CreateGroupFormProps {
  onClose: () => void;
}

const CreateGroupForm: React.FC<CreateGroupFormProps> = ({ onClose }) => {
  const { createGroup } = useGroupsStore();
  const [errorMessage, setErrorMessage] = React.useState<string>();

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, helpers) => {
      setErrorMessage(undefined);
      try {
        await createGroup({
          name: values.name.trim(),
          description: values.description.trim(),
          hashtag: values.hashtag.trim(),
          ...(values.logoUrl ? { logoUrl: values.logoUrl.trim() } : {}),
        });
        helpers.resetForm();
        onClose();
      } catch (err) {
        setErrorMessage(
          `Could not create group. ${err instanceof Error ? err.message : String(err)}`
        );
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        Create New Group
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {errorMessage && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error" variant="body2">
              {errorMessage}
            </Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              id="name"
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              id="description"
              name="description"
              label="Description"
              multiline
              minRows={2}
              value={formik.values.description}
              onChange={formik.handleChange}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              id="hashtag"
              name="hashtag"
              label="Hashtag"
              slotProps={{ input: { startAdornment: <Typography sx={{ mr: 0.5 }}>#</Typography> } }}
              value={formik.values.hashtag}
              onChange={formik.handleChange}
              error={formik.touched.hashtag && Boolean(formik.errors.hashtag)}
              helperText={formik.touched.hashtag && formik.errors.hashtag}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              id="logoUrl"
              name="logoUrl"
              label="Logo URL (optional)"
              value={formik.values.logoUrl}
              onChange={formik.handleChange}
              error={formik.touched.logoUrl && Boolean(formik.errors.logoUrl)}
              helperText={formik.touched.logoUrl && formik.errors.logoUrl}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
          {formik.isSubmitting ? "Creating..." : "Create Group"}
        </Button>
      </DialogActions>
    </form>
  );
};

export const CreateGroupDialog = () => {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <IconButton onClick={() => setOpen(true)} title="Create new group">
        <AddCircleOutlineIcon />
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <CreateGroupForm onClose={() => setOpen(false)} />
      </Dialog>
    </>
  );
};
